/**
 * PII Masking Utilities — Phase 1C of Encryption Build Plan
 * 
 * Masks sensitive data for display in list views and non-detail contexts.
 * Full values should only be shown in detail views with appropriate access.
 * 
 * Examples:
 *   maskPhone("(555) 123-4567") → "•••••4567"
 *   maskEmail("john@example.com") → "j•••@example.com"
 *   maskVIN("1HGBH41JXMN109186") → "•••••••••••09186"
 */

/**
 * Mask a phone number, showing only the last 4 digits.
 * Returns the original value if it's too short to mask meaningfully.
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/[^0-9]/g, '');
  if (digits.length <= 4) return phone; // Too short to mask
  return '•••••' + digits.slice(-4);
}

/**
 * Mask an email address, showing first char + domain.
 * "john.doe@example.com" → "j•••@example.com"
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) return '•••';
  const firstChar = email[0];
  const domain = email.slice(atIndex);
  return `${firstChar}•••${domain}`;
}

/**
 * Mask a VIN, showing only the last 5 characters (partial VIN suffix).
 * "1HGBH41JXMN109186" → "•••••••••••09186"
 */
export function maskVIN(vin: string | null | undefined): string {
  if (!vin) return '';
  const cleaned = vin.replace(/\s/g, '');
  if (cleaned.length <= 5) return cleaned;
  const suffix = cleaned.slice(-5);
  const masked = '•'.repeat(cleaned.length - 5) + suffix;
  return masked;
}

/**
 * Mask a license plate, showing only the last 3 characters.
 * "ABC1234" → "••••234"
 */
export function maskPlate(plate: string | null | undefined): string {
  if (!plate) return '';
  if (plate.length <= 3) return plate;
  return '•'.repeat(plate.length - 3) + plate.slice(-3);
}
